/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorM1,
	ResumeIndicatorM1Data,
} from "@/services/esus/indicator/emulti/m1/getResumeIndicatorM1";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorM1ByHealthTeam,
	ResumeIndicatorM1ByHealthTeamData,
} from "@/services/esus/indicator/emulti/m1/getResumeIndicatorM1ByHealthTeam";
import {
	ResumeCitizenAttendancesIndicatorM1Data,
	getResumeCitizensAttendancesIndicatorM1,
} from "@/services/esus/indicator/emulti/m1/getResumeCitizensAttendancesIndicatorM1";

import { IndicatorEmultiM1Resume } from "./IndicatorEmultiM1Resume";
import { IndicatorEmultiM1HealthTeams } from "./IndicatorEmultiM1HealthTeams";
import { IndicatorEmultiM1Citizens } from "./IndicatorEmultiM1Citizens";

type IndicatorEmultiM1Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEmultiM1({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEmultiM1Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorM1, setResumeIndicatorM1] = useState<ResumeIndicatorM1Data>(
		new ResumeIndicatorM1Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorM1ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizens, setPageNumberCitizens] = useState(1);
	const [searchCitizens, setSearchCitizens] = useState("");
	const [isEvaluatedCitizens, setIsEvaluatedCitizens] = useState<boolean>();
	const debouncedSearchCitizen = useDebounce(searchCitizens, 2000);
	const [paginatedCitizens, setPaginatedCitizens] = useState(
		new Paginated<ResumeCitizenAttendancesIndicatorM1Data>()
	);
	const [isLoadingCitizens, setIsLoadingCitizens] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorM1(),
			handleGetResumeIndicatorM1ByHealthTeam(),
			handleGetResumeCitizensIndicatorM1(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorM1() {
		setResumeIndicatorM1(
			await getResumeIndicatorM1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorM1ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorM1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorM1ByHealthTeam() {
		return (
			await getResumeIndicatorM1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensIndicatorM1(pageNumber?: number) {
		setIsLoadingCitizens(true);
		setPaginatedCitizens(
			await getResumeCitizensAttendancesIndicatorM1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizens(false);
	}

	async function handleGetAllResumeCitizensIndicatorM1() {
		return (
			await getResumeCitizensAttendancesIndicatorM1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: 1,
				pageSize: paginatedCitizens.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorM1ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorM1ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) handleGetResumeCitizensIndicatorM1(pageNumberCitizens);
	}, [pageNumberCitizens]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizens === 1) {
				handleGetResumeCitizensIndicatorM1(pageNumberCitizens);
			} else {
				setPageNumberCitizens(1);
			}
		}
	}, [debouncedSearchCitizen, isEvaluatedCitizens]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("M1 - Média de Atendimentos por Pessoa por eMulti");
	}, [setTitle]);

	return (
		<>
			<IndicatorEmultiM1Resume resume={resumeIndicatorM1} />
			<IndicatorEmultiM1HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorM1ByHealthTeam={
					handleGetAllResumeIndicatorM1ByHealthTeam
				}
			/>
			<IndicatorEmultiM1Citizens
				paginatedCitizens={paginatedCitizens}
				isLoading={isLoadingCitizens}
				search={searchCitizens}
				setSearch={setSearchCitizens}
				isEvaluated={isEvaluatedCitizens}
				setIsEvaluated={setIsEvaluatedCitizens}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberCitizens}
				setPageNumber={setPageNumberCitizens}
				handleGetAllResumeCitizensIndicatorM1={handleGetAllResumeCitizensIndicatorM1}
			/>
		</>
	);
}
